import * as Snackbar from '@synergy/shared/snackbar/data-access';
import * as Yup from 'yup';
import { ArrayHelpers, Field, FieldArray, FieldProps, FormikProps } from 'formik';
import { Box, Button, IconButton, Paper, Stack, TableCell, TableRow } from '@mui/material';
import { CancelIcon } from '@synergy/theme-next';
import { ChangeEvent, Fragment, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { PaloTableViewport } from '@synergy/components-palo';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { PoolCheckbox, PoolTypography, PoolVirtualTable } from '@synergy/components-next';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { TableVirtuosoHandle } from 'react-virtuoso';
import { errorsToTouch, fields } from '@synergy/utils';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';

import { AddParticipantModal } from '../pages/components/add-participant-modal';
import { Contact } from '../../../../../common/models/contact';
import { Customer } from '../../../../../common/models/customer';
import { DemandAccountParticipant } from '../../../models/demand-account-participant';
import { DemandSteps } from '../models/demand-steps';
import { FormikGroupItem } from '../../../../../common/components/new-stepper/formik-group-item';
import { ParticipantsFormValues } from '../models/participants-form-values';
import { PoolAccountContactAutocompleteList } from '../../../../../common/components/autocomplete-list/account-contact-autocomplete-list/pool-account-contact-autocomplete-list';
import { StepData } from '../../../../../common/components/new-stepper/step-data';
import {
    demandDetailParticipantsBaselineSelector,
    demandDetailParticipantsDataSelector,
} from '../store/participants/demand-detail-participants-selectors';
import { demandDetailStepperStateSelector } from '../store/demand-detail-selectors';
import { submitParticipants } from '../store/participants/demand-detail-participants-actions';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

type ParticipantArrayHelpers = ArrayHelpers & {
    form: FormikProps<ParticipantsFormValues>;
    name: string;
};

const fieldNames = fields<ParticipantsFormValues>();
const participantFieldNames = fields<DemandAccountParticipant>();

const validationSchema = Yup.object().shape({
    [fieldNames.participants]: Yup.array()
        .of(
            Yup.object().shape({
                [participantFieldNames.contact]: Yup.object().nullable().required('app.error.required'),
            })
        )
        .test('unique-contacts', 'Contacts must be unique among participants', function (participants) {
            if (!participants) {
                return true;
            }

            const customerGroups = new Map();

            for (let i = 0; i < participants.length; i++) {
                const customerId = participants[i]?.['customer']?.['id'];
                const contactId = participants[i][participantFieldNames.contact]?.['id'];

                if (customerId != null && contactId != null) {
                    if (!customerGroups.has(customerId)) {
                        customerGroups.set(customerId, new Map());
                    }

                    const contactSet = customerGroups.get(customerId);

                    if (contactSet.has(contactId)) {
                        return this.createError({
                            path: `${fieldNames.participants}[${contactSet.get(contactId)}].${
                                participantFieldNames.contact
                            }`,
                            message: 'app.error.participantContact',
                        });
                    }

                    contactSet.set(contactId, i);
                }
            }
            return true;
        }),
});

export const PaloDemandDetailParticipants = (): JSX.Element => {
    const dispatch = useThunkDispatch();
    const participantsData = useAppSelector(demandDetailParticipantsDataSelector);
    // The step as the server has it — see FormikGroupItem.dirtyBaseline.
    const baseline = useAppSelector(demandDetailParticipantsBaselineSelector);
    const readonly = useAppSelector(demandDetailStepperStateSelector).readonlySteps.includes(DemandSteps.Participants);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [openAddParticipantModal, setOpenAddParticipantModal] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const virtualTableRef = useRef<TableVirtuosoHandle>(null);

    useEffect(() => {
        setSelectedIds(
            participantsData.values.participants
                .filter(participant => participant.isSelected)
                .map(participant => participant.id)
        );
    }, [participantsData.values.participants]);

    const handleContactChange =
        (id: number, form: FormikProps<ParticipantsFormValues>) => (contact: Contact | null) => {
            const participant = participantsData.values.participants.find(participant => participant.id === id);
            if (participant) {
                participant.contact = contact;
                form.setValues(participantsData.values);
            }
            setIsDirty(true);
        };

    const handleCheckChange =
        (id: number, form: FormikProps<ParticipantsFormValues>) => (_event: ChangeEvent, checked: boolean) => {
            const participant = participantsData.values.participants.find(participant => participant.id === id);

            if (participant) {
                participant.isSelected = checked;
                form.setValues(participantsData.values);
            }
            if (checked) {
                setSelectedIds([...selectedIds, id]);
            } else {
                setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
            }
            setIsDirty(true);
        };

    const handleCheckAllChange = (_event: ChangeEvent, checked: boolean, helpers: ParticipantArrayHelpers) => {
        participantsData.values.participants.forEach(participant => (participant.isSelected = checked));
        setSelectedIds(checked ? participantsData.values.participants.map(participant => participant.id) : []);

        helpers.form.setValues({
            ...helpers.form.values,
            participants: participantsData.values.participants,
        });

        setIsDirty(true);
    };

    const handleSubmit = (values: StepData<ParticipantsFormValues>) => {
        dispatch(submitParticipants(values));
        setIsDirty(false);
    };

    const handleRemoveParticipant = (id: number, helpers: ParticipantArrayHelpers) => {
        participantsData.values.participants = participantsData.values.participants.filter(
            participant => participant.id !== id
        );

        // needed for formik to re-render
        helpers.form.setValues({
            ...helpers.form.values,
            participants: participantsData.values.participants,
        });

        setIsDirty(true);
    };

    const handleAddParticipant = (customer: Customer, helpers: ParticipantArrayHelpers) => {
        participantsData.values.participants = [
            {
                id: -(new Date().getTime() * participantsData.values.participants.length),
                contact: null,
                customer,
                isSelected: false,
            },
            ...participantsData.values.participants,
        ];

        // needed for formik to re-render
        helpers.form.setValues({
            ...helpers.form.values,
            participants: participantsData.values.participants,
        });

        setIsDirty(true);
        setOpenAddParticipantModal(false);
        virtualTableRef.current?.scrollToIndex({ index: 0, behavior: 'smooth' });
        dispatch(Snackbar.enqueue(PoolNotification.success('app.demands.detail.addParticipant.success')));
    };

    const allSelected = participantsData.values.participants.every(participant => participant.isSelected);

    return (
        <PaloThemeProvider>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <FormikGroupItem
                    groupItemId={DemandSteps.Participants}
                    dirtyBaseline={baseline}
                    initialValues={participantsData.values}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                    initialErrors={participantsData.errors}
                    initialTouched={errorsToTouch(participantsData.errors)}
                    enableReinitialize={true}
                    isDirty={isDirty}
                >
                    <FieldArray name={fieldNames.participants}>
                        {(helpers: ParticipantArrayHelpers) => (
                            <Stack sx={{ height: '100%' }} gap={1.5}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '10.5px',
                                        fontWeight: 640,
                                        letterSpacing: '.08em',
                                        textTransform: 'uppercase',
                                        color: tokens.color.ink3,
                                        pb: '5px',
                                        borderBottom: `1px solid ${tokens.color.line}`,
                                    }}
                                >
                                    <FormattedMessage id="app.demands.step.participants" />
                                    <Box
                                        sx={{
                                            marginLeft: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            textTransform: 'none',
                                            letterSpacing: 0,
                                        }}
                                    >
                                        <Box component="span" sx={{ fontSize: '11px', color: tokens.color.ink3 }}>
                                            <FormattedMessage
                                                id="app.common.selectedParticipants"
                                                values={{ value: selectedIds.length }}
                                            />
                                        </Box>
                                        <Button
                                            variant="text"
                                            size="small"
                                            startIcon={<AddCircleOutline />}
                                            disabled={readonly}
                                            onClick={() => setOpenAddParticipantModal(true)}
                                        >
                                            <FormattedMessage id="app.demands.detail.addParticipant" />
                                        </Button>
                                    </Box>
                                </Box>

                                <PaloTableViewport rows={helpers.form.values.participants.length}>
                                    <Paper elevation={0} sx={{ height: '100%' }}>
                                        <PoolVirtualTable
                                            ref={virtualTableRef}
                                            data={helpers.form.values.participants}
                                            fixedHeaderContent={() => (
                                                <TableRow sx={{ background: tokens.color.surface }}>
                                                    <TableCell sx={{ width: '1%' }}>
                                                        <PoolCheckbox
                                                            variant="selectAllRound"
                                                            checked={allSelected}
                                                            indeterminate={!allSelected && selectedIds.length > 0}
                                                            onChange={(event, checked) =>
                                                                handleCheckAllChange(event, checked, helpers)
                                                            }
                                                            disabled={readonly}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <FormattedMessage id="app.table.column.nameAndLocation" />
                                                    </TableCell>
                                                    <TableCell width={'30%'}>
                                                        <FormattedMessage id="app.table.column.contact" />
                                                    </TableCell>
                                                    <TableCell />
                                                </TableRow>
                                            )}
                                            rowRenderer={participant => {
                                                const index = helpers.form.values.participants.indexOf(participant);

                                                return (
                                                    <Fragment key={participant.id}>
                                                        <TableCell>
                                                            <Field
                                                                name={`${fieldNames.participants}[${index}].${participantFieldNames.isSelected}`}
                                                            >
                                                                {({ form }: FieldProps<Contact>) => (
                                                                    <PoolCheckbox
                                                                        variant="round"
                                                                        checked={participant.isSelected}
                                                                        onChange={handleCheckChange(
                                                                            participant.id,
                                                                            form
                                                                        )}
                                                                        disabled={readonly}
                                                                    />
                                                                )}
                                                            </Field>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack>
                                                                {participant.customer.name}
                                                                <PoolTypography typography="body2" color="grey.700">
                                                                    {participant.customer.mainAddress?.city}
                                                                </PoolTypography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell sx={{ paddingTop: 2, paddingBottom: 1.5 }}>
                                                            <Field
                                                                name={`${fieldNames.participants}[${index}].${participantFieldNames.contact}`}
                                                            >
                                                                {({ meta, form }: FieldProps<Contact>) => (
                                                                    <PoolAccountContactAutocompleteList
                                                                        accountId={participant.customer.id}
                                                                        label={
                                                                            <FormattedMessage id="app.label.contact" />
                                                                        }
                                                                        required={true}
                                                                        value={participant.contact ?? undefined}
                                                                        onSelected={handleContactChange(
                                                                            participant.id,
                                                                            form
                                                                        )}
                                                                        error={!!meta.error && meta.touched}
                                                                        disabled={readonly}
                                                                        showTopics={true}
                                                                        helperText={
                                                                            !!meta.error && meta.touched ? (
                                                                                <FormattedMessage id={meta.error} />
                                                                            ) : undefined
                                                                        }
                                                                    />
                                                                )}
                                                            </Field>
                                                        </TableCell>
                                                        <TableCell>
                                                            {participant.id < 0 && (
                                                                <IconButton
                                                                    color="primary"
                                                                    onClick={() =>
                                                                        handleRemoveParticipant(participant.id, helpers)
                                                                    }
                                                                >
                                                                    <CancelIcon />
                                                                </IconButton>
                                                            )}
                                                        </TableCell>
                                                    </Fragment>
                                                );
                                            }}
                                        />
                                    </Paper>
                                </PaloTableViewport>

                                <AddParticipantModal
                                    open={openAddParticipantModal}
                                    onClose={() => setOpenAddParticipantModal(false)}
                                    onAddParticipant={customer => handleAddParticipant(customer, helpers)}
                                />
                            </Stack>
                        )}
                    </FieldArray>
                </FormikGroupItem>
            </Box>
        </PaloThemeProvider>
    );
};
